import { TimeEntry } from "../store/timeTrackerStore";

// A synthetic, read-only entry produced by folding a chronological run of
// same-client COMPLETED entries together across sub-threshold idle gaps.
// Extends TimeEntry — with endTime always present — so a SmoothedEntry[] drops
// straight into groupEntriesByDay / sumHoursByClient with no signature change,
// while carrying the provenance needed for read-only display and the
// reclaimed-time delta. NEVER persisted: it is derived on read from the raw
// entries, which remain the ground truth.
export interface SmoothedEntry extends TimeEntry {
  endTime: number;
  // Raw entry ids composing this run, earliest-start first (length >= 1).
  sourceIds: string[];
  // Summed duration of the source entries (the actually-tracked time).
  rawMs: number;
  // (endTime - startTime) - rawMs: idle gap time absorbed into the block. Can
  // go negative only if the source entries already overlapped each other — an
  // honest de-duplication of double-counted wall-clock time.
  reclaimedMs: number;
}

// Per-client roll-up of how smoothing changed the totals, plus the grand
// total. byClient is keyed by clientId, with client-less entries under
// "unassigned" — matching sumHoursByClient.
export interface ClientSmoothingTotals {
  rawMs: number;
  smoothedMs: number;
  reclaimedMs: number;
}

export interface SmoothingSummary {
  reclaimedMsTotal: number;
  // How many idle gaps were absorbed across all runs.
  gapsClosed: number;
  byClient: Record<string, ClientSmoothingTotals>;
}

const UNASSIGNED = "unassigned";

type CompletedEntry = TimeEntry & { endTime: number };

const isCompleted = (e: TimeEntry): e is CompletedEntry =>
  e.endTime !== undefined;

const clientKey = (e: TimeEntry): string => e.clientId ?? UNASSIGNED;

// True if any OTHER-client completed entry overlaps the open interval
// (gapStart, gapEnd). `sorted` is ascending by startTime, so we can stop once
// an entry starts at or after the gap ends. A filled gap is a real context
// switch — not an idle break — so it must block the merge, or two clients'
// smoothed runs would overlap and the same wall-clock minute would be billed
// twice.
function gapIsOccupied(
  sorted: CompletedEntry[],
  key: string,
  gapStart: number,
  gapEnd: number,
): boolean {
  if (gapEnd <= gapStart) return false; // overlap/touch: no positive gap
  for (const e of sorted) {
    if (e.startTime >= gapEnd) break;
    if (clientKey(e) === key) continue;
    if (e.endTime > gapStart) return true;
  }
  return false;
}

interface Run {
  members: CompletedEntry[];
  start: number;
  end: number;
}

// Join the members' descriptions in time order, dropping blanks and
// de-duplicating repeats so a run of five "deep work" entries reads as one.
function joinDescriptions(members: CompletedEntry[]): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const m of members) {
    const d = m.description.trim();
    if (d === "" || seen.has(d)) continue;
    seen.add(d);
    parts.push(d);
  }
  return parts.join("; ");
}

function finalizeRun(run: Run): SmoothedEntry {
  const { members, start, end } = run;
  const rawMs = members.reduce((sum, m) => sum + (m.endTime - m.startTime), 0);
  const span = end - start;
  return {
    id: `sm_${members[0].id}`,
    description: joinDescriptions(members),
    startTime: start,
    endTime: end,
    clientId: members[0].clientId,
    sourceIds: members.map((m) => m.id),
    rawMs,
    reclaimedMs: span - rawMs,
  };
}

// Pure. Folds consecutive same-client COMPLETED entries into one block when
// the gap between them is <= thresholdMs AND no other client's work falls in
// that gap. Running entries (no endTime) are ignored — there is nothing to
// smooth until they stop. Input may be unsorted and may contain overlaps: each
// client's entries are sorted by startTime, and an overlap (gap <= 0) always
// merges. Output is sorted by startTime ascending; every input entry surfaces
// in exactly one run, so unmerged entries come back as single-source
// SmoothedEntries (reclaimedMs 0).
export function smoothEntries(
  entries: TimeEntry[],
  thresholdMs: number,
): SmoothedEntry[] {
  const completed = entries.filter(isCompleted);
  const sorted = [...completed].sort((a, b) => a.startTime - b.startTime);

  const byClient = new Map<string, CompletedEntry[]>();
  for (const e of sorted) {
    const key = clientKey(e);
    const list = byClient.get(key);
    if (list) list.push(e);
    else byClient.set(key, [e]);
  }

  const result: SmoothedEntry[] = [];
  for (const [key, list] of byClient) {
    // `list` is already ascending (built from `sorted`).
    let run: Run | null = null;
    for (const entry of list) {
      if (run === null) {
        run = { members: [entry], start: entry.startTime, end: entry.endTime };
        continue;
      }
      const gap = entry.startTime - run.end;
      if (
        gap <= thresholdMs &&
        !gapIsOccupied(sorted, key, run.end, entry.startTime)
      ) {
        run.members.push(entry);
        run.end = Math.max(run.end, entry.endTime);
      } else {
        result.push(finalizeRun(run));
        run = { members: [entry], start: entry.startTime, end: entry.endTime };
      }
    }
    if (run !== null) result.push(finalizeRun(run));
  }

  return result.sort((a, b) => a.startTime - b.startTime);
}

// Pure. Rolls a smoothed list up into the reclaimed-time delta (grand total +
// per client) and the count of gaps absorbed. Drives the analytics summary.
export function summarizeSmoothing(
  smoothed: SmoothedEntry[],
): SmoothingSummary {
  const byClient: Record<string, ClientSmoothingTotals> = {};
  let reclaimedMsTotal = 0;
  let gapsClosed = 0;

  for (const e of smoothed) {
    const key = e.clientId ?? UNASSIGNED;
    const totals =
      byClient[key] ?? (byClient[key] = { rawMs: 0, smoothedMs: 0, reclaimedMs: 0 });
    totals.rawMs += e.rawMs;
    totals.smoothedMs += e.endTime - e.startTime;
    totals.reclaimedMs += e.reclaimedMs;
    reclaimedMsTotal += e.reclaimedMs;
    gapsClosed += e.sourceIds.length - 1;
  }

  return { reclaimedMsTotal, gapsClosed, byClient };
}
